import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';
import { useBusiness } from '../contexts/BusinessContext';
import { AI_TERMS_VERSION } from '../lib/nexusAiConstants';

/**
 * Shared first-run gate for Nexus AI Help + Voice.
 * Ready when: this user accepted AI terms AND this business claimed (or already
 * has) the one-time 1M welcome tokens.
 */
export function useNexusAiOnboarding() {
  const { user } = useAuth();
  const { selectedBusiness } = useBusiness();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [welcomeClaimed, setWelcomeClaimed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [showIntro, setShowIntro] = useState(false);
  const [pendingAction, setPendingAction] = useState<'help' | 'voice' | null>(null);

  const reload = useCallback(async () => {
    if (!user?.id || !supabase) {
      setLoaded(true);
      return;
    }
    const cacheKey = `nexus_ai_terms_accepted:${user.id}`;
    try {
      if (localStorage.getItem(cacheKey) === String(AI_TERMS_VERSION)) {
        setTermsAccepted(true);
      }
    } catch { /* ignore */ }

    const termsPromise = supabase
      .from('user_ai_terms_acceptance')
      .select('version')
      .eq('user_id', user.id)
      .maybeSingle();

    const claimPromise = selectedBusiness?.id
      ? supabase
          .from('ai_welcome_token_claims')
          .select('business_id')
          .eq('business_id', selectedBusiness.id)
          .maybeSingle()
      : Promise.resolve({ data: null });

    const [{ data: terms }, { data: claim }] = await Promise.all([termsPromise, claimPromise]);
    const accepted = (terms?.version ?? 0) >= AI_TERMS_VERSION;
    setTermsAccepted(accepted);
    setWelcomeClaimed(Boolean(claim?.business_id));
    try {
      if (accepted) localStorage.setItem(cacheKey, String(AI_TERMS_VERSION));
      else localStorage.removeItem(cacheKey);
    } catch { /* ignore */ }
    setLoaded(true);
  }, [user?.id, selectedBusiness?.id]);

  useEffect(() => {
    setLoaded(false);
    void reload();
  }, [reload]);

  // Keep FAB + App instances in sync when either completes the flow.
  useEffect(() => {
    const onComplete = () => {
      setTermsAccepted(true);
      setWelcomeClaimed(true);
    };
    window.addEventListener('nexus-ai-onboarding-complete', onComplete);
    return () => window.removeEventListener('nexus-ai-onboarding-complete', onComplete);
  }, []);

  const needsOnboarding = !termsAccepted || (!!selectedBusiness?.id && !welcomeClaimed);

  const requestAccess = useCallback((action: 'help' | 'voice'): boolean => {
    if (!needsOnboarding) return true;
    setPendingAction(action);
    setShowIntro(true);
    return false;
  }, [needsOnboarding]);

  const markComplete = useCallback(() => {
    setTermsAccepted(true);
    setWelcomeClaimed(true);
    try {
      if (user?.id) localStorage.setItem(`nexus_ai_terms_accepted:${user.id}`, String(AI_TERMS_VERSION));
    } catch { /* ignore */ }
    window.dispatchEvent(new Event('nexus-ai-onboarding-complete'));
  }, [user?.id]);

  return {
    loaded,
    needsOnboarding,
    termsAccepted,
    welcomeClaimed,
    showIntro,
    setShowIntro,
    pendingAction,
    setPendingAction,
    requestAccess,
    markComplete,
    reload,
  };
}
